import { type Theme } from "@mui/material";
import { type EChartsOption } from "echarts";
import merge from "lodash/merge";

import { roundToPrecision } from "../../../../../utils/numbers";

export const getCommonChartOptions = (theme: Theme, overrideOptions?: EChartsOption): EChartsOption => {
  const baseOptions: EChartsOption = {
    animation: false,
    grid: {
      top: 10,
      left: 65,
      right: 10,
      bottom: 50,
    },
    yAxis: {
      type: "value",
      axisLine: {
        lineStyle: {
          color: theme.palette.text.primary,
        },
      },
      axisLabel: {
        color: theme.palette.text.primary,
      },
      splitLine: {
        lineStyle: {
          color: theme.palette.divider,
        },
      },
    },
    legend: {
      textStyle: {
        color: theme.palette.text.primary,
      },
      bottom: 0,
      type: "scroll",
      icon: "circle",
    },
    tooltip: {
      appendTo: document.body,
      trigger: "axis",
      backgroundColor: theme.palette.background.paper,
      borderColor: theme.palette.divider,
      textStyle: {
        color: theme.palette.text.primary,
      },
    },
  };

  if (overrideOptions) {
    return merge({}, baseOptions, overrideOptions);
  }

  return baseOptions;
};

export const formatTimestamp = (timestamp: string): string => {
  const date = new Date(timestamp);
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  });
};

export const formatTimestampAxis = (formattedDate: string): string => formattedDate.split(",")[0];
export const formatTimestampHourAxis = (formattedDate: string): string => formattedDate.split(",")[1];

export const formatMemory = (
  value: string | number,
  decimals: number = 4,
  originUnit: "B" | "KB" | "MB" | "GB" = "MB"
): string => {
  const unitMultipliers: Record<string, number> = {
    B: 1 / 1024 ** 3,
    KB: 1 / 1024 ** 2,
    MB: 1 / 1024,
    GB: 1,
  };

  const multiplier = unitMultipliers[originUnit] || unitMultipliers.MB;
  const convertedValue = value ? +value * multiplier : 0;

  return `${roundToPrecision(convertedValue, decimals).toString()} GB`;
};

export const formatCpu = (value: string | number, decimals: number = 0): string =>
  `${value ? roundToPrecision(+value, decimals).toString() : 0} cores`;

export const formatMoney = (value: string, precision = 4, currencySymbol = "$"): string => {
  const num = parseFloat(value);
  if (isNaN(num)) {
    return `${currencySymbol}0`;
  }
  const absValue = Math.abs(num);
  if (absValue >= 1e9) {
    return `${currencySymbol}${roundToPrecision(num / 1e9, precision)}B`;
  } else if (absValue >= 1e6) {
    return `${currencySymbol}${roundToPrecision(num / 1e6, precision)}M`;
  } else if (absValue >= 1e3) {
    return `${currencySymbol}${roundToPrecision(num / 1e3, precision)}k`;
  }
  return `${currencySymbol}${roundToPrecision(num, precision)}`;
};
